import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type {
  AppointmentStatus,
  AppointmentsByPatientIdRequest,
  AppointmentsDetailsResponse,
  DateFilter,
} from '@/types/scheduling-api';

/**
 * Patient appointments list (tab 6 of the patient-edit shell — PROD-734).
 *
 * Wire (Flutter `scheduling_data_source_impl.dart:1081` —
 * `getAppointmentsByPatient` + `toGetByPatientIdJson` serializer):
 *
 *   POST /api/Appointment/ByPatientId
 *        body: AppointmentsByPatientIdRequest
 *        → AppointmentsDetailsResponse
 *
 * The body shape mirrors Flutter's `toGetByPatientIdJson` — `searchText`
 * defaults to `""` (NOT null), `practitioners` / `status` / `location` /
 * `types` default to empty arrays. `filter` is required; Flutter passes
 * `CalendarType.Day.name` (i.e. the literal string "Day") even when no
 * date range is selected — the backend uses it as a sort key on the
 * server side, not a filter clause. We do the same.
 *
 * PR A scope (read-only): single page, no UI filters. Filters defer to PR B.
 *
 * **Cache:** `staleTime: 30_000` (30s). Appointment status changes during a
 * session (Booked → Arrived → In Session → Completed) but refetching on
 * every tab focus would hammer the endpoint. 30s is the same window
 * scheduling uses for fresh-but-cached list queries.
 *
 * **Guard:** the query is `enabled: !!params.patientId` — never fires
 * with an empty/undefined id. Even though the parent shell only mounts
 * this tab when a patient is loaded, the hook guards defensively so a
 * future caller that passes a stale prop can't trigger a "no patient
 * scope" POST. Samer PR #148 review 2026-05-15.
 */

const DEFAULT_DATE_FILTER: DateFilter = 'Day';

export interface UsePatientAppointmentsParams {
  patientId: string | null | undefined;
  pageNumber?: number;
  pageSize?: number;
  startDate?: string | null;
  endDate?: string | null;
  filter?: DateFilter;
  searchText?: string;
  /**
   * Filter arrays — mirror Flutter `toGetByPatientIdJson`. Default to empty
   * (the PR A read-only mount passes none). `status` ships FULL status
   * objects, NOT codes (Flutter serializes `status.map((x) => x.toJson())`).
   * `practitioners` / `practices` / `location` ship id strings.
   */
  practitioners?: string[];
  practices?: string[];
  status?: AppointmentStatus[];
  location?: string[];
}

export function usePatientAppointments(params: UsePatientAppointmentsParams) {
  const orgId = useAuthStore((s) => s.user?.organizationId) ?? '';
  const practiceId = useAuthStore((s) => s.user?.practiceId) ?? '';

  const body: AppointmentsByPatientIdRequest & {
    searchText?: string;
    LoggedInUserTimezone?: string;
    organizationId?: string;
    practiceId?: string;
  } = {
    searchText: params.searchText ?? '',
    LoggedInUserTimezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    filter: params.filter ?? DEFAULT_DATE_FILTER,
    startDate: params.startDate ?? undefined,
    endDate: params.endDate ?? undefined,
    organizationId: orgId,
    practiceId,
    practitioners: params.practitioners ?? [],
    practices: params.practices ?? [],
    // Flutter ships FULL status objects (`status.map((x) => x.toJson())`),
    // not bare codes — the backend round-trips the whole record.
    status: params.status ?? [],
    location: params.location ?? [],
    types: [],
    // Flutter `toGetByPatientIdJson` always sends this (false on this screen).
    isTimeSelected: false,
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? 20,
    patientId: params.patientId ?? '',
  };

  return useQuery({
    queryKey: [
      'patient-appointments',
      params.patientId,
      body.pageNumber,
      body.pageSize,
      body.searchText,
      body.startDate,
      body.endDate,
      body.filter,
      // Filter arrays participate in the cache key so changing any filter
      // refetches. Serialized (stable order from the caller).
      body.practitioners,
      body.practices,
      body.status,
      body.location,
    ],
    queryFn: ({ signal }) =>
      post<AppointmentsDetailsResponse>(
        '/api/Appointment/ByPatientId',
        body,
        { signal },
      ),
    enabled: !!params.patientId,
    placeholderData: (prev) => prev,
    // 30s cache so tab-focus refetches don't hammer the endpoint.
    // Samer PR #148 review 2026-05-15.
    staleTime: 30_000,
  });
}
